import { Address } from '@/form/types/address';
import { StripeProviderProps } from '@/checkout/providers/stripe-provider';
import { useCallback, useEffect, useState } from 'react';
import { useElements, useStripe } from '@stripe/react-stripe-js';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface SubmitStripePaymentProps
  extends Pick<StripeProviderProps, 'customerSessionClientSecret'> {
  billingAddress?: Partial<Address>;
}

const GENERIC_ERROR_MESSAGE =
  'An error occurred while submitting the form. Check your payment details and try again.';

/**
 * Hook that returns a method for submitting a payment to Stripe. Errors are appended to form data as
 * `stripe_error` which should be handled by the action and displayed to the user.
 * @returns
 */
export function useSubmitStripePayment({
  customerSessionClientSecret,
  billingAddress,
}: SubmitStripePaymentProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitStripe, setSubmitStripe] =
    useState<(formData: FormData) => Promise<FormData>>();

  const submitStripeCallback = useCallback(
    async (formData: FormData) => {
      const name = String(formData.get('name'));
      // Use the billing address from props if available, otherwise from form
      const address1 = billingAddress?.address1 ?? String(formData.get('address1'));
      const address2 = billingAddress?.address2 ?? String(formData.get('address2'));
      const city = billingAddress?.city ?? String(formData.get('city'));
      const state = billingAddress?.state ?? String(formData.get('state'));
      const zipcode = billingAddress?.zipcode ?? String(formData.get('zipcode'));

      // Check if Stripe.js has finished loading
      if (!stripe || !elements) {
        formData.append('stripe_error', GENERIC_ERROR_MESSAGE);
        return formData;
      }

      // Check if the customer session client secret is available
      if (!customerSessionClientSecret) {
        formData.append('stripe_error', GENERIC_ERROR_MESSAGE);
        return formData;
      }

      const { error: submitError } = await elements.submit();

      if (submitError) {
        formData.append('stripe_error', submitError?.message ?? GENERIC_ERROR_MESSAGE);
        return formData;
      }

      const { error: createConfirmationTokenError, confirmationToken } =
        await stripe.createConfirmationToken({
          elements,
          params: {
            payment_method_data: {
              billing_details: {
                name: name,
                email: null,
                phone: null,
                address: {
                  line1: address1,
                  line2: address2,
                  city: city,
                  state: state,
                  postal_code: zipcode,
                  country: 'US',
                },
              },
            },
          },
        });

      if (createConfirmationTokenError) {
        // Error messages provided by Stripe are user facing (see https://docs.stripe.com/api/errors#errors-message)
        formData.append(
          'stripe_error',
          createConfirmationTokenError?.message ?? GENERIC_ERROR_MESSAGE
        );
        return formData;
      }

      formData.append('stripe_confirmation_token', confirmationToken.id);

      return formData;
    },
    [
      billingAddress?.address1,
      billingAddress?.address2,
      billingAddress?.city,
      billingAddress?.state,
      billingAddress?.zipcode,
      customerSessionClientSecret,
      elements,
      stripe,
    ]
  );

  // This hook should only return a submission method if a Stripe payment element exists within the form (a payment method is optional on some forms)
  useEffect(() => {
    if (elements?.getElement('payment')) {
      setSubmitStripe(() => submitStripeCallback);
    }
  }, [elements, submitStripeCallback]);

  return submitStripe;
}
